/**
 * WorkshopDayDrawerRow — one selectable workshop row inside the BON-142 day
 * drawer ({@link WorkshopDayDrawer}): bold time · tinted tree-type pill · bold
 * venue · city · region · seats left (warn colour when low) · price/seat.
 */
import type { PickerWorkshop } from './pickerWorkshop';
import { WorkshopDayDrawerRowTag } from './WorkshopDayDrawerRowTag';
import {
  formatPickerTime,
  formatPricePerSeat,
  isLowSeats,
  pickerAvailability,
  rowSeatsLabel,
} from './WorkshopPickerHelpers';

export interface WorkshopDayDrawerRowProps {
  workshop: PickerWorkshop;
  selected: boolean;
  onSelect: (id: string) => void;
}

export function WorkshopDayDrawerRow({ workshop, selected, onSelect }: WorkshopDayDrawerRowProps) {
  const { disabled } = pickerAvailability(workshop);
  const seatsLabel = rowSeatsLabel(workshop);
  const warn = !disabled && isLowSeats(workshop.seatsLeft);

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={() => onSelect(workshop.id)}
      className={`w-full rounded-xl border p-3 text-left transition ${
        disabled
          ? 'cursor-not-allowed border-stone-200 opacity-50 dark:border-stone-700'
          : selected
            ? 'border-moss-600 bg-moss-50/60 dark:bg-moss-900/20'
            : 'border-stone-200 hover:border-stone-300 dark:border-stone-700'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex-shrink-0 text-sm font-bold text-bark-900 dark:text-stone-100">
            {formatPickerTime(workshop.localStartsAt)}
          </span>
          <WorkshopDayDrawerRowTag workshop={workshop} />
        </div>
        <span className="flex-shrink-0 text-sm font-semibold text-bark-900 dark:text-stone-100">
          {formatPricePerSeat(workshop.priceCents)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0 truncate text-xs text-stone-500 dark:text-stone-400">
          <span className="font-semibold text-bark-900 dark:text-stone-100">
            {workshop.venueName}
          </span>
          {workshop.venueCity ? ` · ${workshop.venueCity}` : ''}
          {workshop.regionName ? ` · ${workshop.regionName}` : ''}
        </div>
        <span
          className="flex-shrink-0 text-xs font-semibold"
          style={{ color: warn ? 'var(--nb-warn)' : 'var(--nb-muted)' }}
        >
          {seatsLabel}
        </span>
      </div>
    </button>
  );
}
